import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { filter, map, switchMap, catchError, withLatestFrom } from 'rxjs/operators';

import * as koddiAdsActions from '../../actions/koddi-ads/koddi-ads.actions';
import { MicroServicesService } from '../../modules/core/services/micro-services.service';
import { IKoddiAdsData, IKoddiAdsPayload, IKoddiAdsResponse } from '../../models/cobrand-model';
import { HttpResponseModel } from '../../shared/interfaces/generic-response.interface';
import * as fromRoot from '../../reducers';

/** Helper: check if pageId is not loaded yet */
const isNotLoaded = (adsByPageId: Record<string, unknown>, pageId: string) : boolean =>
  !adsByPageId || !Object.prototype.hasOwnProperty.call(adsByPageId, pageId);

/** Helper: normalize API response into the final payload (null means no ads) */
function normalizeResponse(response: HttpResponseModel<IKoddiAdsPayload>): IKoddiAdsData[] | null {
  if (Number(response.status) === 404) {
    return null;
  }
  const advertisements = response.payload?.advertisements;
  return advertisements?.length ? advertisements : null;
}

@Injectable()
export class KoddiAdsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject<Store<fromRoot.AppState>>(Store);
  private readonly microServicesService = inject(MicroServicesService);

  /** Load ads when not already present for the pageId */
  loadKoddiAds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(koddiAdsActions.loadKoddiAds),
      withLatestFrom(this.store.select(fromRoot.getKoddiAdsState)),
      filter(([action, koddiState]) => isNotLoaded(koddiState.adsByPageId, action.payload.pageId)),
      switchMap(([action]) =>
        this.microServicesService.getKoddiAds({
          pageId: action.payload.pageId,
          sessionCookie: action.payload.sessionCookie
        }).pipe(
          map((response) => {
            const data = normalizeResponse(response);
            return koddiAdsActions.loadKoddiAdsSuccess({
              payload: {
                pageId: action.payload.pageId,
                data
              }
            });
          }),
          catchError((error) => {
            const errorResp = error?.error as IKoddiAdsResponse | undefined;
            const status = Number(error?.status);
            const errorCode = errorResp?.error?.errorCode;
            const traceId = errorResp?.error?.traceId;

            if (status === 404 || errorCode === 'RMS-0104') {
              return of(koddiAdsActions.loadKoddiAdsSuccess({
                payload: {
                  pageId: action.payload.pageId,
                  data: null
                }
              }));
            }

            return of(koddiAdsActions.loadKoddiAdsFailure({
              payload: {
                pageId: action.payload.pageId,
                error,
                errorCode,
                traceId
              }
            }));
          })
        )
      )
    )
  );
}
